import React, {Key, ReactNode} from "react";
import {HighlightAnnotation} from "./HighlightAnnotation";
import {PortalDocumentInterface} from "../../../document/PortalDocument";
import {WidgetAnnotation} from "./WidgetAnnotation";
import {SquareAnnotation} from "./SquareAnnotation";
import {UnderlineAnnotation} from "./UnderlineAnnotation";
import {SquigglyAnnotation} from "./SquigglyAnnotation";
import {StrikeoutAnnotation} from "./StrikeoutAnnotation";
import {CircleAnnotation} from "./CircleAnnotation";
import {InkAnnotation} from "./InkAnnotation";
import {PolylineAnnotation} from "./PolylineAnnotation";
import {PolygonAnnotation} from "./PolygonAnnotation";
import {ConfidentialStampAnnotation} from "./stamps/ConfidentialStampAnnotation";
import {ApprovedStampAnnotation} from "./stamps/ApprovedStampAnnotation";
import {RevisedStampAnnotation} from "./stamps/RevisedStampAnnotation";
import {ReviewedStampAnnotation} from "./stamps/ReviewedStampAnnotation";
import {ReceivedStampAnnotation} from "./stamps/ReceivedStampAnnotation";
import {WitnessStampAnnotation} from "./stamps/WitnessStampAnnotation";
import {InitialHereStampAnnotation} from "./stamps/InitialHereStampAnnotation";
import {SignHereStampAnnotation} from "./stamps/SignHereStampAnnotation";
import {AcceptedStampAnnotation} from "./stamps/AcceptedStampAnnotation";
import {RejectedStampAnnotation} from "./stamps/RejectedStampAnnotation";
import {NotApprovedStampAnnotation} from "./stamps/NotApprovedStampAnnotation";
import {FinalStampAnnotation} from "./stamps/FinalStampAnnotation";
import {CompletedStampAnnotation} from "./stamps/CompletedStampAnnotation";
import {VoidStampAnnotation} from "./stamps/VoidStampAnnotation";
import {DraftStampAnnotation} from "./stamps/DraftStampAnnotation";
import {ForPublicReleaseStampAnnotation} from "./stamps/ForPublicReleaseStampAnnotation";
import {NotForPublicReleaseStampAnnotation} from "./stamps/NotForPublicReleaseStampAnnotation";
import {ForCommentStampAnnotation} from "./stamps/ForCommentStampAnnotation";
import {PreliminaryResultsStampAnnotation} from "./stamps/PreliminaryResultsStampAnnotation";
import {InformationOnlyStampAnnotation} from "./stamps/InformationOnlyStampAnnotation";
import {FileAnnotation} from "./FileAnnotation";
import {LineAnnotation} from "./LineAnnotation";
import {FreetextAnnotation} from "./FreetextAnnotation";
import {TextAnnotation} from "./TextAnnotation";
import {RedactAnnotation} from "./RedactAnnotation";
import {AnnotationSubType} from "./AnnotationSubType";
import {Annotation, AnnotationInterface} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import {ExperimentalStampAnnotation} from "./stamps/ExperimentalStampAnnotation";
import {TopSecretStampAnnotation} from "./stamps/TopSecretStampAnnotation";
import {DepartmentalStampAnnotation} from "./stamps/DepartmentalStampAnnotation";
import {SoldStampAnnotation} from "./stamps/SoldStampAnnotation";
import {ExpiredStampAnnotation} from "./stamps/ExpiredStampAnnotation";
import {AsIsStampAnnotation} from "./stamps/AsIsStampAnnotation";
import {LinkAnnotation} from "./LinkAnnotation";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface AnnotationFactoryProperties extends I18nGlobals {
	pageNumber: number;
	zoomValue: number;
	key?: Key | null;
	context: PortalContext;
	portalDocument: Immutable<PortalDocumentInterface>;
}

export default class AnnotationFactory {
	private constructor() {
		// not constructable
	}

	public static createInstance(
		immutableAnnotation: Immutable<AnnotationInterface>,
		props: AnnotationFactoryProperties
	): ReactNode {
		let annotation: Annotation = Annotation.fromJson(immutableAnnotation.get());

		switch (annotation.subType) {
			case AnnotationSubType.HIGHLIGHT:
				return <HighlightAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.WIDGET:
				return <WidgetAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.SQUARE:
				return <SquareAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.UNDERLINE:
				return <UnderlineAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.SQUIGGLY:
				return <SquigglyAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.STRIKEOUT:
				return <StrikeoutAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.CIRCLE:
				return <CircleAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.INK:
				return <InkAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.POLYLINE:
				return <PolylineAnnotation {...props} annotation={immutableAnnotation}/>
			case AnnotationSubType.POLYGON:
				return <PolygonAnnotation {...props} annotation={immutableAnnotation}/>
			case AnnotationSubType.STAMP:
				let type: string = FunctionHelper.applyValue(annotation.iconName, Annotation.getIconNameDefault());

				switch (type) {
					case '#DConfidential':
					case 'SBConfidential':
					case 'Confidential':
						return <ConfidentialStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DApproved':
					case 'SBApproved':
					case 'Approved':
						return <ApprovedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DRevised':
					case 'SBRevised':
					case 'Revised':
						return <RevisedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DReviewed':
					case 'SBReviewed':
					case 'Reviewed':
						return <ReviewedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DReceived':
					case 'SBReceived':
					case 'Received':
						return <ReceivedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DWitness':
					case 'SHWitness':
					case 'Witness':
						return <WitnessStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DInitialHere':
					case 'SHInitialHere':
					case 'InitialHere':
						return <InitialHereStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DSignHere':
					case 'SHSignHere':
					case 'SignHere':
						return <SignHereStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DAccepted':
					case 'SHAccepted':
					case 'Accepted':
						return <AcceptedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DRejected':
					case 'SBRejected':
					case 'Rejected':
						return <RejectedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DNotApproved':
					case 'SBNotApproved':
					case 'NotApproved':
						return <NotApprovedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DFinal':
					case 'SBFinal':
					case 'Final':
						return <FinalStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DCompleted':
					case 'SBCompleted':
					case 'Completed':
						return <CompletedStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DVoid':
					case 'SBVoid':
					case 'Void':
						return <VoidStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DDraft':
					case 'SBDraft':
					case 'Draft':
						return <DraftStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DForPublicRelease':
					case 'SBForPublicRelease':
					case 'ForPublicRelease':
						return <ForPublicReleaseStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DNotForPublicRelease':
					case 'SBNotForPublicRelease':
					case 'NotForPublicRelease':
						return <NotForPublicReleaseStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DForComment':
					case 'SBForComment':
					case 'ForComment':
						return <ForCommentStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DPreliminaryResults':
					case 'SBPreliminaryResults':
					case 'PreliminaryResults':
						return <PreliminaryResultsStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DInformationOnly':
					case 'SBInformationOnly':
					case 'InformationOnly':
						return <InformationOnlyStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DExperimental':
					case 'SBExperimental':
					case 'Experimental':
						return <ExperimentalStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DTopSecret':
					case 'SBTopSecret':
					case 'TopSecret':
						return <TopSecretStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DDepartmental':
					case 'SBDepartmental':
					case 'Departmental':
						return <DepartmentalStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DSold':
					case 'SBSold':
					case 'Sold':
						return <SoldStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DExpired':
					case 'SBExpired':
					case 'Expired':
						return <ExpiredStampAnnotation {...props} annotation={immutableAnnotation}/>;
					case '#DAsIs':
					case 'SBAsIs':
					case 'AsIs':
						return <AsIsStampAnnotation {...props} annotation={immutableAnnotation}/>;
				}
				break;
			case AnnotationSubType.FILEATTACHMENT:
				return <FileAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.LINE:
				return <LineAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.FREETEXT:
				return <FreetextAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.TEXT:
				return <TextAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.REDACT:
				return <RedactAnnotation {...props} annotation={immutableAnnotation}/>;
			case AnnotationSubType.LINK:
				return <LinkAnnotation {...props} annotation={immutableAnnotation}/>;
		}

		return null;
	}
}